import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ManageCompanies from '@/assets/svg/icon_managecompanie.svg';
import Products from '@/assets/svg/icon_manageproducts.svg';
import Users from '@/assets/svg/icon_users.svg';
import theme from '@/common/theme';
import { Col } from '@/components/grid/grid-component';
import Link from '@/components/link/link-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import { APP_CONTEXT } from '@/utils/context';
import { BodyTextLarge, Heading1Text } from '@/utils/global-fonts';
import Styled from './dashboard-style';

/**
 * @returns {JSX.Element}
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(APP_CONTEXT);

  return (
    <Styled.Container>
      <Styled.StyledMainRow>
        <Styled.StyledGridCol className="first">
          <Styled.StyledDiv>
            <Styled.PersonIcon color={theme.COLOR_PRIMARY} size={24} />
            <BodyTextLarge color={theme.COLOR_WHITE}>
              Hello {currentUser?.first_name} {currentUser?.last_name}!
            </BodyTextLarge>
          </Styled.StyledDiv>
          <Styled.StyledDiv>
            <Heading1Text color={theme.COLOR_WHITE}>
              Welcome to the ETTX Portal
            </Heading1Text>
          </Styled.StyledDiv>
        </Styled.StyledGridCol>
        <Styled.StyledGridCol>
          <PermissionWrapper permissions={['company.create']}>
            <Styled.StyledCol>
              <Styled.StyledTile
                onClick={() => navigate('/app/company-management/new')}
              >
                <Styled.StyledTileRow
                  align="center"
                  justify="center"
                  direction="column"
                >
                  <Styled.CompanyIcon color={theme.COLOR_PRIMARY} size={32} />
                  <Link to="/">+ Add Company</Link>
                </Styled.StyledTileRow>
              </Styled.StyledTile>
            </Styled.StyledCol>
          </PermissionWrapper>
          <PermissionWrapper permissions={['user.create']}>
            <Styled.StyledCol>
              <Styled.StyledTile
                onClick={() => navigate('/app/user-management/users/new')}
              >
                <Styled.StyledTileRow
                  align="center"
                  justify="center"
                  direction="column"
                >
                  <Styled.UserIcon color={theme.COLOR_PRIMARY} size={32} />
                  <Link to="/">+ Add User</Link>
                </Styled.StyledTileRow>
              </Styled.StyledTile>
            </Styled.StyledCol>
          </PermissionWrapper>
          <PermissionWrapper permissions={['productCategory.create']}>
            <Styled.StyledCol>
              <Styled.StyledTile
                onClick={() =>
                  navigate('/app/categories', { state: { dashboard: true } })
                }
              >
                <Styled.StyledTileRow
                  align="center"
                  justify="center"
                  direction="column"
                >
                  <Styled.CategoryIcon color={theme.COLOR_PRIMARY} size={32} />
                  <Link to="/">+ Add Category</Link>
                </Styled.StyledTileRow>
              </Styled.StyledTile>
            </Styled.StyledCol>
          </PermissionWrapper>
          <PermissionWrapper permissions={['price.create']}>
            <Styled.StyledCol>
              <Styled.StyledTile
                onClick={() =>
                  currentUser?.company_id
                    ? navigate(
                        `/app/company-management/view/${currentUser?.company_id}/pricing`,
                      )
                    : navigate(`/app/company-management/`)
                }
              >
                <Styled.StyledTileRow
                  align="center"
                  justify="center"
                  direction="column"
                >
                  <Styled.PricelistIcon color={theme.COLOR_PRIMARY} size={32} />
                  <Link to="/">+ Add Pricelist</Link>
                </Styled.StyledTileRow>
              </Styled.StyledTile>
            </Styled.StyledCol>
          </PermissionWrapper>
        </Styled.StyledGridCol>
      </Styled.StyledMainRow>
      <Styled.StyledSecondRow>
        <PermissionWrapper permissions={['user.read']}>
          <Styled.StyledSecondRowCol xs={12} sm={12}>
            <Styled.StyledSecondRowTile
              onClick={() => navigate('/app/user-management')}
            >
              <Styled.StyledSecondTileRow align="center" direction="row">
                <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
                  <Styled.StyledInnerTileRow direction="column">
                    <Styled.StyledHeading2Text>
                      User Management
                    </Styled.StyledHeading2Text>
                    <BodyTextLarge>{`View all >`}</BodyTextLarge>
                  </Styled.StyledInnerTileRow>
                </Col>
                <Col xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                  <Styled.StyledInnerTileRow justify="flex-end">
                    <Styled.StyledIconDiv>
                      <Styled.Hero src={Users} />
                    </Styled.StyledIconDiv>
                  </Styled.StyledInnerTileRow>
                </Col>
              </Styled.StyledSecondTileRow>
            </Styled.StyledSecondRowTile>
          </Styled.StyledSecondRowCol>
        </PermissionWrapper>
        <PermissionWrapper permissions={['company.read']}>
          <Styled.StyledSecondRowCol xs={12} sm={12}>
            <Styled.StyledSecondRowTile
              onClick={() => navigate('/app/company-management')}
            >
              <Styled.StyledSecondTileRow align="center" direction="row">
                <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
                  <Styled.StyledInnerTileRow direction="column">
                    <Styled.StyledHeading2Text>
                      Manage Companies
                    </Styled.StyledHeading2Text>
                    <BodyTextLarge>{`View all >`}</BodyTextLarge>
                  </Styled.StyledInnerTileRow>
                </Col>
                <Col xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                  <Styled.StyledInnerTileRow justify="flex-end">
                    <Styled.StyledIconDiv>
                      <Styled.Hero src={ManageCompanies} />
                    </Styled.StyledIconDiv>
                  </Styled.StyledInnerTileRow>
                </Col>
              </Styled.StyledSecondTileRow>
            </Styled.StyledSecondRowTile>
          </Styled.StyledSecondRowCol>
        </PermissionWrapper>
        <PermissionWrapper permissions={['product.read']}>
          <Styled.StyledSecondRowCol xs={12} sm={12}>
            <Styled.StyledSecondRowTile
              onClick={() =>
                currentUser?.company_id
                  ? navigate(
                      `/app/company-management/view/${currentUser?.company_id}/products`,
                    )
                  : navigate(`/app/company-management/`)
              }
            >
              <Styled.StyledSecondTileRow align="center" direction="row">
                <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
                  <Styled.StyledInnerTileRow direction="column">
                    <Styled.StyledHeading2Text>
                      Manage Products
                    </Styled.StyledHeading2Text>
                    <BodyTextLarge>{`Select a supplier >`}</BodyTextLarge>
                  </Styled.StyledInnerTileRow>
                </Col>
                <Col xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                  <Styled.StyledInnerTileRow justify="flex-end">
                    <Styled.StyledIconDiv>
                      <Styled.Hero src={Products} />
                    </Styled.StyledIconDiv>
                  </Styled.StyledInnerTileRow>
                </Col>
              </Styled.StyledSecondTileRow>
            </Styled.StyledSecondRowTile>
          </Styled.StyledSecondRowCol>
        </PermissionWrapper>
      </Styled.StyledSecondRow>
    </Styled.Container>
  );
};

export default Dashboard;
